import { Box, Input, SimpleGrid, Text } from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import InputMask from "react-input-mask";
import Select from "react-select";
// import { raceOptions, tribeOptions } from "../../../utils/case/caseTypeOption";
import { useDropdownOptions } from "../../../hooks/useDropdownOptions";
import { selectStyles } from "../../../utils/selectStyel";

const genderOptions = [
  { label: "Female", value: "F" },
  { label: "Male", value: "M" },
];

const twinsOptions = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const normalizeGender = (genderStr) => {
  if (!genderStr) return "";
  const val = genderStr.toUpperCase();
  if (val === "MALE") return "M";
  if (val === "FEMALE") return "F";
  if (val === "M" || val === "F") return val;
  return null;
};

// ✅ Define this BEFORE useEffect
const formatSSN = (value) => {
  if (typeof value !== "string") return ""; // prevent .replace error
  const digits = value.replace(/\D/g, "").slice(0, 9);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 5));
  if (digits.length > 5) parts.push(digits.slice(5));
  return parts.join("-");
};

const EditInformationForm = ({ initialData = {}, onChange }) => {
  const [formData, setFormData] = useState(initialData);
  const { options: raceOptions } = useDropdownOptions("RACE");
  const { options: tribeOptions } = useDropdownOptions(
    "JURISDICTIONAL_ASSIGNMENT",
  );

  useEffect(() => {
    const normalized = {
      ...initialData,
      BIRTH_DATE: initialData.BIRTH_DATE
        ? moment(initialData.BIRTH_DATE, "YYYYMMDD").format("YYYY-MM-DD")
        : "",
      GENDER: normalizeGender(initialData.GENDER),
      SSN: initialData.SSN ? formatSSN(initialData.SSN) : "",
    };
    setFormData(normalized);
  }, [initialData]);

  const handleChange = (field, value) => {
    let rawValue = value;

    if (field === "SSN") {
      rawValue = value.replace(/\D/g, "").slice(0, 9);
      value = formatSSN(rawValue);
    }

    const updated = {
      ...formData,
      [field]: value,
    };

    const transformed = {
      ...updated,
      BIRTH_DATE:
        field === "BIRTH_DATE"
          ? moment(value, "YYYY-MM-DD").format("YYYYMMDD")
          : updated.BIRTH_DATE,
      ...(field === "SSN" && { SSN: rawValue }),
    };

    setFormData(updated);
    onChange?.(transformed);
  };

  const fields = [
    "FIRST_NAME",
    "LAST_NAME",
    "MIDDLE_NAME",
    "MAIDEN_NAME",
    "ALIAS",
    "PREFIX",
    "BIRTH_DATE",
    "GENDER",
    "RACE",
    "TRIBE",
    "SSN",
    "TWINS",
  ];

  const isMultiSelectField = (field) => field === "RACE" || field === "TRIBE";
  const isSingleSelectField = (field) =>
    field === "GENDER" || field === "TWINS";

  return (
    <Box>
      <Text mb={4} fontWeight={500}>
        Information
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        {fields.map((field) => (
          <Box key={field}>
            <Text mb={1} fontWeight="400">
              {field.replace(/_/g, " ")}
            </Text>
            {field === "BIRTH_DATE" ? (
              <Input
                type="date"
                value={formData[field] || ""}
                onChange={(e) => handleChange(field, e.target.value)}
              />
            ) : field === "SSN" ? (
              <InputMask
                mask="999-99-9999"
                value={formData[field] || ""}
                onChange={(e) => handleChange(field, e.target.value)}
              >
                {(inputProps) => <Input {...inputProps} type="text" />}
              </InputMask>
            ) : isMultiSelectField(field) ? (
              <Select
                menuPortalTarget={document.body}
                isMulti
                styles={selectStyles}
                options={field === "RACE" ? raceOptions : tribeOptions}
                value={(field === "RACE" ? raceOptions : tribeOptions).filter(
                  (opt) => (formData[field] || []).includes(opt.value),
                )}
                onChange={(selected) =>
                  handleChange(
                    field,
                    selected ? selected.map((opt) => opt.value) : [],
                  )
                }
              />
            ) : isSingleSelectField(field) ? (
              <Select
                styles={selectStyles}
                options={field === "GENDER" ? genderOptions : twinsOptions}
                value={
                  (field === "GENDER" ? genderOptions : twinsOptions).find(
                    (opt) => opt.value === formData[field],
                  ) || null
                }
                onChange={(selected) =>
                  handleChange(field, selected?.value || "")
                }
                menuPortalTarget={document.body}
              />
            ) : (
              <Input
                value={formData[field] || ""}
                onChange={(e) => handleChange(field, e.target.value)}
              />
            )}
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default EditInformationForm;
